import React, { useEffect, useMemo, useRef, useState } from 'react';
import APP_CONFIG from '../config/appConfig';
import {
  aceitavel,
  buildAnnouncement,
  defaultConfig,
  deveFalar,
  INTERVALO_REPETICAO_MS,
  loadConfig,
  montarHistorico,
  registrarAnuncio,
  rememberSpoken,
  saveConfig,
  vezesAnunciadas,
} from '../chamadas/announcePolicy';
import { createSpeechQueue } from '../chamadas/speechQueue';

function createBrowserSpeaker() {
  const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined;
  return {
    supported: Boolean(synth),
    voices() {
      return synth?.getVoices?.() || [];
    },
    speak(text, { voiceURI, onend, onerror }) {
      if (!synth || typeof window.SpeechSynthesisUtterance !== 'function') {
        onerror?.();
        return;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'pt-BR';
      const voices = synth.getVoices?.() || [];
      const voice = voices.find((item) => item.voiceURI === voiceURI)
        || voices.find((item) => /pt-BR|pt_BR/i.test(item.lang));
      if (voice) utterance.voice = voice;
      utterance.onend = () => onend?.();
      utterance.onerror = () => onerror?.();
      synth.resume?.();
      synth.speak(utterance);
    },
  };
}

async function defaultFetcher(url, options) {
  const resposta = await fetch(url, options);
  return {
    ok: resposta.ok,
    json: () => resposta.json(),
  };
}

function novoDispositivo() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `painel-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function PainelChamadas({
  fetcher = defaultFetcher,
  speaker = createBrowserSpeaker(),
  storage = window.localStorage,
  now = () => Date.now(),
  apiUrl = APP_CONFIG.API_URL,
}) {
  const [config, setConfig] = useState(() => loadConfig(storage));
  const [form, setForm] = useState(() => loadConfig(storage) || defaultConfig());
  const [unlocked, setUnlocked] = useState(false);
  const [atual, setAtual] = useState(null);
  const [recentes, setRecentes] = useState([]);
  const [offline, setOffline] = useState(false);
  const [hora, setHora] = useState(() => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
  const [configurando, setConfigurando] = useState(() => !loadConfig(storage)?.token);
  const configRef = useRef(config);
  const unlockedRef = useRef(unlocked);
  const esperaRef = useRef([]);
  const iniciadasRef = useRef(new Set());
  const timersRef = useRef([]);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  useEffect(() => {
    unlockedRef.current = unlocked;
  }, [unlocked]);

  useEffect(() => {
    const relogio = window.setInterval(() => {
      setHora(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
    }, 1000);
    return () => {
      window.clearInterval(relogio);
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const speakerRef = useRef(speaker);
  speakerRef.current = speaker;
  const fila = useMemo(() => createSpeechQueue((call, hooks) => {
    const atualConfig = configRef.current;
    const mensagem = buildAnnouncement({
      paciente: call.paciente,
      sala: call.sala,
      tipo: call.tipo,
      templateComSala: atualConfig.templateComSala,
      templateSemSala: atualConfig.templateSemSala,
    });
    if (!mensagem) {
      hooks.fail();
      return;
    }
    console.info('[painel-chamadas] anuncio', {
      id: call.id,
      cnes: call.cnes,
      temSala: Boolean(call.sala),
    });
    speakerRef.current.speak(mensagem, {
      voiceURI: atualConfig.voiceURI,
      onend: () => hooks.done(),
      onerror: () => hooks.fail(),
    });
  }), []);

  function persistir(proxima) {
    configRef.current = proxima;
    setConfig(proxima);
    saveConfig(storage, proxima);
  }

  async function confirmar(chaves) {
    const atualConfig = configRef.current;
    if (!chaves.length) return;
    const resposta = await fetcher(`${apiUrl}/api/painel-chamadas/ack`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Painel-Token': atualConfig.token,
      },
      body: JSON.stringify({
        cnes: atualConfig.cnes,
        deviceId: atualConfig.deviceId,
        sala: atualConfig.sala,
        keys: chaves,
      }),
    });
    if (!resposta.ok) throw new Error('ack');
    persistir({
      ...configRef.current,
      pendingAcks: (configRef.current.pendingAcks || []).filter((chave) => !chaves.includes(chave)),
    });
  }

  function marcarFalada(call, confirmarAck) {
    const atualConfig = configRef.current;
    const spoken = rememberSpoken(atualConfig.spoken, call.dedupKey, now());
    const pendingAcks = confirmarAck
      ? [...new Set([...(atualConfig.pendingAcks || []), call.dedupKey])]
      : atualConfig.pendingAcks;
    persistir({ ...atualConfig, spoken, pendingAcks });
    if (confirmarAck) {
      confirmar([call.dedupKey]).catch(() => {
        console.info('[painel-chamadas] confirmacao adiada', { id: call.id });
      });
    }
  }

  function agendarRepeticao(call) {
    if (iniciadasRef.current.has(`timer-${call.id}`)) return;
    if (!deveFalar(configRef.current.anuncios, call.id)) return;
    iniciadasRef.current.add(`timer-${call.id}`);
    const timer = window.setTimeout(() => {
      iniciadasRef.current.delete(`timer-${call.id}`);
      if (!deveFalar(configRef.current.anuncios, call.id)) {
        marcarFalada(call, true);
        return;
      }
      const anuncios = registrarAnuncio(configRef.current.anuncios, call.id, now());
      persistir({ ...configRef.current, anuncios });
      enfileirar(call, true);
    }, INTERVALO_REPETICAO_MS);
    timersRef.current.push(timer);
  }

  function enfileirar(call, ultima) {
    fila.enqueue({
      ...call,
      filaId: `${call.id}:${ultima ? 2 : 1}`,
      onDone: () => {
        if (ultima) marcarFalada(call, true);
        else agendarRepeticao(call);
      },
      onFail: () => {
        console.info('[painel-chamadas] falha de reproducao', { id: call.id, temSala: Boolean(call.sala) });
        if (ultima) marcarFalada(call, true);
        else agendarRepeticao(call);
      },
    });
  }

  function tratarChamada(call) {
    const atualConfig = configRef.current;
    if (!atualConfig.audio) {
      marcarFalada(call, true);
      return;
    }
    if (!unlockedRef.current) {
      if (!esperaRef.current.some((item) => item.id === call.id)) esperaRef.current.push(call);
      return;
    }
    if (!deveFalar(atualConfig.anuncios, call.id)) {
      marcarFalada(call, true);
      return;
    }
    if (vezesAnunciadas(atualConfig.anuncios, call.id) === 1) {
      if (!iniciadasRef.current.has(call.id)) agendarRepeticao(call);
      return;
    }
    iniciadasRef.current.add(call.id);
    const anuncios = registrarAnuncio(atualConfig.anuncios, call.id, now());
    persistir({ ...configRef.current, anuncios });
    enfileirar(call, false);
  }

  useEffect(() => {
    if (!config?.token || !config?.cnes || configurando) return undefined;
    let ativo = true;
    let consultando = false;

    const consultar = async () => {
      if (consultando) return;
      consultando = true;
      const atualConfig = configRef.current;
      try {
        if (atualConfig.pendingAcks?.length) await confirmar(atualConfig.pendingAcks);
        const params = new URLSearchParams({
          cnes: atualConfig.cnes,
          deviceId: atualConfig.deviceId,
          sala: atualConfig.sala || '',
        });
        const resposta = await fetcher(`${apiUrl}/api/painel-chamadas?${params.toString()}`, {
          headers: { 'X-Painel-Token': atualConfig.token },
        });
        if (!resposta.ok) throw new Error('consulta');
        const corpo = await resposta.json();
        if (!ativo) return;
        setOffline(false);
        const recebidas = (corpo.data?.announcements || []).filter((call) => aceitavel(call, atualConfig));
        const novas = recebidas.filter((call) => !atualConfig.spoken?.[call.dedupKey] && deveFalar(atualConfig.anuncios, call.id));
        if (recebidas.length) {
          setAtual(recebidas[recebidas.length - 1]);
          setRecentes((lista) => montarHistorico(lista, recebidas));
        }
        const chavesAtivas = new Set(novas.map((call) => call.dedupKey));
        fila.retainOnly(chavesAtivas);
        recebidas.forEach((call) => {
          if (atualConfig.spoken?.[call.dedupKey] || !deveFalar(atualConfig.anuncios, call.id)) {
            if (!atualConfig.spoken?.[call.dedupKey]) marcarFalada(call, true);
            return;
          }
          tratarChamada(call);
        });
      } catch {
        if (ativo) setOffline(true);
      } finally {
        consultando = false;
      }
    };

    consultar();
    const intervalo = window.setInterval(consultar, Math.min(Math.max(config.intervaloMs || 2000, 1000), 60000));
    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
  }, [config?.token, config?.cnes, config?.deviceId, config?.intervaloMs, configurando, apiUrl, fetcher, fila]);

  function salvar(event) {
    event.preventDefault();
    const cnes = String(form.cnes || '').replace(/\D/g, '');
    if (cnes.length < 6 || cnes.length > 7 || !form.token.trim()) return;
    const proxima = {
      ...defaultConfig(),
      ...config,
      ...form,
      cnes,
      token: form.token.trim(),
      sala: form.sala.trim(),
      intervaloMs: Number(form.intervaloMs) || 2000,
      deviceId: config?.deviceId || form.deviceId || novoDispositivo(),
      spoken: config?.spoken || {},
      anuncios: config?.anuncios || {},
      pendingAcks: config?.pendingAcks || [],
    };
    persistir(proxima);
    setConfigurando(false);
    setUnlocked(false);
  }

  function ativarAudio() {
    speaker.speak('Painel de chamadas ativado.', {
      voiceURI: configRef.current?.voiceURI,
      onend: () => undefined,
      onerror: () => undefined,
    });
    setUnlocked(true);
    unlockedRef.current = true;
    esperaRef.current.splice(0).forEach((call) => tratarChamada(call));
  }

  if (configurando || !config?.token) {
    return (
      <main className="min-h-screen bg-slate-950 px-6 py-10 text-white">
        <form onSubmit={salvar} className="mx-auto max-w-xl space-y-4">
          <h1 className="text-3xl font-semibold">Painel de chamadas</h1>
          <p className="text-slate-300">Informe a unidade deste televisor. A página fica neste navegador.</p>
          <label className="block text-sm">Token do painel
            <input className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" value={form.token} onChange={(event) => setForm({ ...form, token: event.target.value })} required />
          </label>
          <label className="block text-sm">CNES da unidade
            <input className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" value={form.cnes} onChange={(event) => setForm({ ...form, cnes: event.target.value })} inputMode="numeric" required />
          </label>
          <label className="block text-sm">Sala ou consultório, se este painel for de um destino só
            <input className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" value={form.sala} onChange={(event) => setForm({ ...form, sala: event.target.value })} />
          </label>
          <label className="block text-sm">Intervalo da consulta, em milissegundos
            <input className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" type="number" min="1000" max="60000" value={form.intervaloMs} onChange={(event) => setForm({ ...form, intervaloMs: Number(event.target.value) })} />
          </label>
          <label className="block text-sm">Mensagem quando houver sala. Use {'{paciente}'}, {'{sala}'} e {'{tipo}'}.
            <textarea className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" value={form.templateComSala} onChange={(event) => setForm({ ...form, templateComSala: event.target.value })} />
          </label>
          <label className="block text-sm">Mensagem quando a sala não vier na chamada
            <textarea className="mt-1 w-full rounded-xl bg-slate-900 px-4 py-3" value={form.templateSemSala} onChange={(event) => setForm({ ...form, templateSemSala: event.target.value })} />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.audio} onChange={(event) => setForm({ ...form, audio: event.target.checked })} />
            Áudio habilitado neste painel
          </label>
          <button type="submit" className="rounded-xl bg-emerald-500 px-5 py-3 font-semibold text-slate-950">Salvar e abrir painel</button>
        </form>
      </main>
    );
  }

  const unidade = atual?.unidade || recentes.find((item) => item.unidade)?.unidade || '';

  return (
    <main className="flex min-h-screen flex-col bg-[#eef3f8] text-slate-900">
      <header className="bg-[#0b63c6] px-6 py-6 text-white sm:px-10">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl font-bold text-[#0b63c6]">+</div>
            <div>
              <p className="text-xl font-bold leading-none">Saúde</p>
              <p className="mt-1 text-xs uppercase tracking-wide text-blue-100">Atenção Primária</p>
            </div>
          </div>
          <div className="text-right">
            {unidade ? <p className="text-lg font-medium">{unidade}</p> : null}
            <p className="text-3xl font-light">{hora}</p>
            <div className="mt-3 flex justify-end gap-2">
              {!unlocked && config.audio && (
                <button type="button" onClick={ativarAudio} className="rounded-full bg-white px-4 py-1.5 text-sm font-semibold text-[#0b63c6]">
                  Ativar áudio
                </button>
              )}
              <button type="button" onClick={() => setConfigurando(true)} className="rounded-full border border-white/40 px-4 py-1.5 text-sm">
                Configurar
              </button>
            </div>
          </div>
        </div>
        <div className="mt-10 min-h-[220px]">
          {atual ? (
            <>
              {atual.tipo ? (
                <span className="inline-flex rounded-full bg-[#e85a9b] px-4 py-1 text-sm font-semibold">{atual.tipo}</span>
              ) : null}
              {atual.sala ? (
                <h1 data-testid="sala" className="mt-4 text-5xl font-bold uppercase tracking-wide sm:text-7xl">{atual.sala}</h1>
              ) : null}
              <p data-testid="paciente" className="mt-3 text-4xl font-semibold sm:text-6xl">{atual.paciente}</p>
            </>
          ) : (
            <h1 className="text-4xl font-semibold text-blue-100">Aguardando chamadas desta unidade</h1>
          )}
        </div>
      </header>
      <section className="flex-1 px-6 py-6 sm:px-10">
        {offline && <p className="mb-4 text-sm text-red-700">Sem conexão com a API. Nova tentativa em instantes.</p>}
        {!speaker.supported && <p className="mb-4 text-sm text-slate-600">Este navegador não oferece síntese de voz. A chamada continua visível.</p>}
        <h2 className="text-lg font-semibold text-[#0b63c6]">Últimas chamadas</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {recentes.map((item) => (
            <article key={item.dedupKey} className="rounded-xl bg-white px-5 py-4 shadow-sm">
              {item.sala ? <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.sala}</p> : null}
              <p className="mt-2 text-2xl font-bold text-slate-900">{item.paciente}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
